import get from 'lodash.get';
import {
    VirtualizedTableItem,
    VirtualizedTableColumns
} from 'services/virtualizedTable/types';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
import { Product } from 'services/products/types';
import { selectProductsArray } from 'services/products/selectors';
import { selectUnitTypeOptions } from 'services/unitTypes/selectors';
import { selectProductTypeOptions } from 'services/productTypes/selectors';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import { selectIsAdmin } from 'services/usersMe/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_DROPDOWN_MULTIPLE
} from 'components/VirtualizedTable/constants';
import { selectActiveIngredientsOptions } from 'services/activeIngredients/selectors';
import { ActiveIngredient } from 'services/activeIngredients/types';

const selectActiveProducts = createDeepEqualSelector(
    [selectProductsArray],
    (products) => products.filter((product) => !product.deletedAt)
);

const selectProductsTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectUnitTypeOptions,
        selectProductTypeOptions,
        selectActiveIngredientsOptions
    ],
    (
        tableColumnWidths,
        unitTypeOptions,
        productTypeOptions,
        activeIngredientsOptions
    ): VirtualizedTableColumns<Product> => {
        const result: VirtualizedTableColumns<Product> = [
            {
                label: 'Name',
                value: 'name',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired',
                disableUpdating: true
            },
            {
                label: 'Product Type',
                value: 'sprayProductTypeId',
                width: 250,
                type: FIELD_TYPE_DROPDOWN,
                options: productTypeOptions,
                validators: ['isRequiredNumber', 'isInteger'],
                disableUpdating: true
            },
            {
                label: 'Witholding Period (days)',
                value: 'witholdingPeriodDays',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isRequiredNumber',
                    'isExactlyDivisibleByHalf',
                    'minValue0',
                    'maxValue999Half'
                ]
            },
            {
                label: 'Re-Entry Period (days)',
                value: 'reEntryPeriodDays',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isRequiredNumber',
                    'isExactlyDivisibleByHalf',
                    'minValue0',
                    'maxValue999Half'
                ]
            },
            {
                label: 'Unit Type',
                value: 'unitTypeId',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                options: unitTypeOptions,
                validators: ['isRequiredNumber', 'isInteger']
            },
            {
                label: 'Active Ingredients',
                value: 'activeIngredientIds',
                width: 150,
                type: FIELD_TYPE_DROPDOWN_MULTIPLE,
                options: activeIngredientsOptions
            },
            {
                label: 'Primary Target',
                value: 'primaryTargetName',
                width: 150,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            },
            {
                label: 'Secondary Target',
                value: 'secondaryTargetName',
                width: 150,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            },
            {
                label: 'CropSure Id',
                value: 'cropSureId',
                width: 250,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            },
            {
                label: 'Minimum Tree Age (months)',
                value: 'minTreeAgeMonths',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: ['isInteger', 'minValue0', 'maxValue1000']
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectProductItems = createDeepEqualSelector(
    selectActiveProducts,
    (activeProducts) =>
        activeProducts.map((product: Product): VirtualizedTableItem => {
            const activeIngredients: ActiveIngredient[] = get(
                product,
                'activeIngredients',
                []
            );
            const activeIngredientIds = activeIngredients.map(
                (ingredient: ActiveIngredient) => ingredient.id
            );

            return {
                ...product,
                key: product.uuid,
                activeIngredientIds
            };
        })
);

const makeSelectProductItems = (): typeof selectProductItems =>
    selectProductItems;

const selectIsWritable = createDeepEqualSelector(
    [selectIsAdmin, selectCurrentSeasonIsComplete],
    (isAdmin: boolean, currentSeasonIsComplete: boolean): boolean =>
        isAdmin && !currentSeasonIsComplete
);

export {
    selectActiveProducts,
    selectProductsTableColumns,
    selectProductItems,
    makeSelectProductItems,
    selectIsWritable
};
